// UserUtil.ts
// Utility functions for user-related operations.
// Used for user lookup, formatting, and permission checks.

import { User } from "./User";
import {Ticket, TicketType} from "./Ticket";
import { Group } from "./Group";
import {getKey} from "./KeyRef";

export function isMine(user: User, ticket: Ticket) {
  return ticket.createdBy._id === user._id
      || ticket.assignees.some(assignee => assignee._id === user._id)
      || user.adminRoles.some(role => ticket.roleAssignees.includes(role));
}

/**
 * Check if the user has an admin role for the ticket's group
 *
 * @param user the user to check
 * @param ticket the ticket to check
 * @param groups the available groups cache
 */
export function isAdminForTicket(
  user: User,
  ticket: Ticket,
  groups: Record<string, Group>
): boolean {
  return groups[ticket.group]?.adminRoles.some((it) =>
    user.adminRoles.includes(getKey(it))
  );
}

/**
 * Staff capabilities for ticket actions (assign, context panel) — matches SingleTicketView.isAdmin
 * and useTicketPermissions.isAdmin: group role, ticket role assignment, or supervisor.
 */
export function isStaffForTicket(
  user: User,
  ticket: Ticket,
  groups: Record<string, Group>
): boolean {
  if (user.adminRoles == null) return false;
  return (
    !!groups[ticket.group]?.adminRoles.some((it) =>
      user.adminRoles.includes(getKey(it))
    ) ||
    ticket.roleAssignees.some((it) => user.adminRoles.includes(it)) ||
    user.power >= 100
  );
}

export function isAdmin(user: User, adminRoles: string[]): boolean {
  return (
    adminRoles.some((adminRole) => user.adminRoles.includes(adminRole)) ||
    isSupervisor(user)
  );
}

export function isSupervisor(user: User): boolean {
  return user.power >= 100;
}

export function isAssignedToTicket(visualId: string, ticket: Ticket): boolean {
    return ticket.assignees.some((it) => it.visualId == visualId);
}

export function isRoleAssignedToTicket(role: string, ticket: Ticket): boolean {
    return ticket.roleAssignees.includes(role);
}

export function isInTicket(user: User, ticket: Ticket, groups: Record<string, Group>): boolean {
  return ticket.createdBy._id == user._id
      // If the ticket is unassigned, and I'm in the group of the ticket
      || (ticket.assignees.length == 0 && ticket.roleAssignees.length == 0 && isAdminForTicket(user, ticket, groups))
      // If I'm assigned to the ticket either by myself or the group
      || isAssignedInTicket(user, ticket)
      // If the ticket is a report, and I'm the accused or a partner
      || (ticket.type == TicketType.REPORT && (ticket.reportAccused?.visualId == user.visualId || ticket.reportPartners.find(it => it.visualId == user.visualId) != null))
      // If the ticket is a report and I'm a jury member
      || isJuryMember(user, ticket)
}

/**
 * Check if user is a jury member for the ticket
 * @param user the user to check
 * @param ticket the ticket to check
 */
export function isJuryMember(user: User, ticket: Ticket): boolean {
  return ticket.juryMembers?.some(m => m._id === user._id) || false;
}

function isAccountInNullableTicketName(name: string, ticket: Ticket): boolean {
  return ticket.createdBy.name == name
      || (ticket.type == TicketType.REPORT && (ticket.reportAccused?.name == name || ticket.reportPartners.find(it => it.name == name) != null))
}

function isAccountInNullableTicketVisualId(visualId: string, ticket: Ticket): boolean {
  return ticket.createdBy.visualId == visualId
      || (ticket.type == TicketType.REPORT && (ticket.reportAccused?.visualId == visualId || ticket.reportPartners.find(it => it.visualId == visualId) != null))
}

/**
 * Return true if the account is in the ticket, or if the ticket is null
 */
export function isAccountInNullableTicket(nameOrVisualId: string, ticket: Ticket | null | undefined): boolean {
  if (!ticket) {
    return true;
  }
  return isAccountInNullableTicketName(nameOrVisualId, ticket) || isAccountInNullableTicketVisualId(nameOrVisualId, ticket);
}

export function isAssignedInTicket(user: User, ticket: Ticket): boolean {
  return ticket.assignees.some(it => it.visualId == user.visualId) || ticket.roleAssignees.some(it => user.adminRoles.includes(it))
}

